import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import ArrayStore from 'devextreme/data/array_store';
import DataSource from 'devextreme/data/data_source';
import type { ValueChangedEvent as CheckBoxValueChangedEvent } from 'devextreme/ui/check_box';
import type {
  ClosedEvent as TagBoxClosedEvent,
  ValueChangedEvent as TagBoxValueChangedEvent,
} from 'devextreme/ui/tag_box';

export interface ReportEntityFilterOption {
  value: string;
  label: string;
  searchText: string;
  type: 'RMD' | 'MGA';
}

@Component({
  selector: 'ag-shr-report-entity-filter',
  templateUrl: './report-entity-filter.component.html',
  styleUrls: ['./report-entity-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportEntityFilterComponent {
  @Input() set options(options: ReportEntityFilterOption[]) {
    this.uniqueOptions = Array.from(new Map((options ?? []).map(option => [option.value, option])).values());
    this.dataSource = new DataSource({
      store: new ArrayStore({ key: 'value', data: this.uniqueOptions }),
      group: { selector: 'type', desc: true },
      paginate: false,
    });
  }

  /**
   * Held by identity, not just by content.
   *
   * dxTagBox compares its `value` option by reference, so handing it an equal-but-new array makes
   * it re-apply and emit `onValueChanged` again; the parent then stores another new array and the
   * two bounce off each other until Angular gives up with NG0103 and the page stops responding.
   * Ignoring an update that says the same thing keeps that from starting.
   */
  @Input() set value(next: string[]) {
    const values = next ?? [];
    if (!ReportEntityFilterComponent.haveSameValues(values, this.selectedValues)) {
      this.selectedValues = values;
    }
  }

  get value(): string[] {
    return this.selectedValues;
  }
  @Input() disabled = false;
  @Input() placeholder = 'Filter by RMD or MGA';
  @Input() maxDisplayedTags = 3;
  @Input() height: number | undefined;

  @Output() readonly valueChange = new EventEmitter<string[]>();
  @Output() readonly filterClosed = new EventEmitter<void>();

  private selectedValues: string[] = [];
  protected uniqueOptions: ReportEntityFilterOption[] = [];
  protected dataSource = new DataSource({ store: [], paginate: false });

  /**
   * Undefined is the indeterminate state, which dxCheckBox only keeps with three-state behaviour
   * on. Handing it `null` against `enableThreeStateBehavior: false` made the widget coerce the
   * value back to false on every change-detection pass, so a partly selected group locked the
   * page in an NG0103 loop — which is every group the moment one of its options is picked.
   */
  protected getGroupSelectionState(type: ReportEntityFilterOption['type']): boolean | undefined {
    const groupValues = this.uniqueOptions.filter(option => option.type === type).map(option => option.value);
    const selectedValues = new Set(this.value);
    const selectedCount = groupValues.filter(value => selectedValues.has(value)).length;

    if (!selectedCount) return false;
    if (selectedCount === groupValues.length) return true;
    return undefined;
  }

  protected onGroupSelectionChanged(type: ReportEntityFilterOption['type'], event: CheckBoxValueChangedEvent): void {
    if (!event.event) return;

    const groupValues = this.uniqueOptions.filter(option => option.type === type).map(option => option.value);
    const nextValues = new Set(this.value);

    if (event.value) {
      groupValues.forEach(value => nextValues.add(value));
    } else {
      groupValues.forEach(value => nextValues.delete(value));
    }

    this.valueChange.emit([...nextValues]);
  }

  protected onValueChanged(event: TagBoxValueChangedEvent): void {
    const values = Array.isArray(event.value) ? event.value.map(String) : [];
    // The widget also reports values it was given; only a real change is worth telling the parent.
    if (ReportEntityFilterComponent.haveSameValues(values, this.selectedValues)) {
      return;
    }

    this.selectedValues = values;
    this.valueChange.emit(values);
  }

  private static haveSameValues(left: readonly string[], right: readonly string[]): boolean {
    return left.length === right.length && left.every((value, index) => value === right[index]);
  }

  protected onClosed(event: TagBoxClosedEvent): void {
    event.component.option('searchValue', '');
    this.filterClosed.emit();
  }
}
